import { useRouteQuery } from '@/core/browser/route-query.composable';
import { QueryKeys } from '@/shared/config/route-query.config';
import { computed, reactive, watch } from 'vue';
import {
    BaseProductItem,
    BasketCheckoutModel,
    BasketCheckoutModelSummary,
    BasketDeliveryAddress,
    BasketInvoiceAddress,
    DeliveryTimesForCustomerDelivery,
    DeliveryOptionModel,
    EarliestCustomerDeliveryDates,
    PickUpPointsForDelivery,
    PickUpTimesForCustomerDelivery,
    SetDeliveryTimeRequest,
    SetCustomerDeliveryCommentRequest,
    STOREClientService,
    AddInvoiceAddressToBasketRequest,
    PaymentOption,
    PaymentOptionType,
    DeliveryDateAndFreightPriceForSplitItemsInCustomerDelivery,
    CheckOutPosRequest,
    CheckOutOnlinePaymentRequest,
    CustomerAddressSuggestionModel,
    ParcelShopModel,
    CheckOutBankTransferPaymentRequest,
} from '@/storecontext/shared/api/server-apis';
import useBasket from '@/storecontext/basket/useBasket';
import utilitiesService from '@/shared/utilities.service';
import { hardRedirect } from '@/core/navigation/redirect.service';
import { usePosPayment } from '@/storecontext/checkout/usePosPayment';

export const getCustomerAddressSuggestionByEmailMessageId = 'chokeAddressSuggestionsByEmailMessages';

export interface ICheckoutState {
    step: number;
    basket: BasketCheckoutModel;
    deliveryInfo: DeliverableItemsDeliveryInfo;
    selectedPaymentOption: PaymentOption | null;
    paymentAddressValidationHandler: (() => Promise<boolean>) | null;
    isPaymentReady: boolean;
    itemIdsOutOfStock: number[];
    hasDeliveryDateChanged: boolean;
    showBankTransferPaymentDialog: boolean;
}

export interface DeliverableItemsDeliveryInfo {
    [id: number]: EarliestCustomerDeliveryDates,
}

const state: ICheckoutState = reactive({
    step: 0,
    basket: {} as BasketCheckoutModel,
    deliveryInfo: {} as DeliverableItemsDeliveryInfo,
    selectedPaymentOption: null,
    paymentAddressValidationHandler: null,
    isPaymentReady: false,
    itemIdsOutOfStock: [] as number[],
    hasDeliveryDateChanged: false,
    showChooseSalesPersonDialog: false,
    showBankTransferPaymentDialog: false,
});

export function useCheckout() {
    const { basketId } = useBasket();
    const stepQuery = useRouteQuery<number>(QueryKeys.CheckoutStep);
    const { launch: launchPOS } = usePosPayment();

    const step = computed<number>(() => {
        return state.step;
    });

    const isAddressStep = computed<boolean>(() => {
        return state.step === 0;
    });

    const isDeliverableStep = computed<boolean>(() => {
        return state.step !== 0;
    });

    const deliverableCount = computed<number>(() => {
        return state.basket.deliverables?.length ?? 0;
    });

    const isLastStep = computed<boolean>(() => {
        return state.basket.deliverables?.length === state.step ?? false;
    });

    const items = computed<BaseProductItem[]>(() => {
        return state.basket.items ?? [] as BaseProductItem[];
    });

    const summary = computed<BasketCheckoutModelSummary>(() => {
        return state.basket.summary ?? {} as BasketCheckoutModelSummary;
    });

    const deliveryInfo = computed<DeliverableItemsDeliveryInfo>(() => {
        return state.deliveryInfo ?? {} as DeliverableItemsDeliveryInfo;
    });

    const invoiceAddress = computed<BasketInvoiceAddress | undefined>(() => {
        return state.basket.invoiceAddress;
    });

    const selectedPaymentOption = computed<PaymentOption | null>(() => {
        return state.selectedPaymentOption;
    });

    const defaultPaymentOption = computed<PaymentOption | null>(() => {
        const posOption = state.basket.paymentOptions?.find(option => option.paymentOptionType === PaymentOptionType.Pos);
        return posOption ?? null;
    });

    const basket = computed<BasketCheckoutModel>(() => {
        return state.basket ?? {} as BasketCheckoutModel;
    });

    const isPaymentReady = computed<boolean>(() => {
        return state.isPaymentReady;
    });

    const itemIdsOutOfStock = computed<number[]>(() => {
        return state.itemIdsOutOfStock;
    });

    const hasDeliveryDateChanged = computed<boolean>(() => {
        return state.hasDeliveryDateChanged;
    });

    const showBankTransferPaymentDialog = computed(() => {
        return state.showBankTransferPaymentDialog;
    });

    const init = async(): Promise<void> => {
        setIsPaymentReady(false);
        const response = await STOREClientService.getBasketForCheckOut(basketId.value).then(utilitiesService.unwrapModel);
        state.basket = response ?? {} as BasketCheckoutModel;
    };

    const setDeliveryAddress = async(deliverableId: number, request: BasketDeliveryAddress): Promise<void> => {
        const response = await STOREClientService
            .setDeliveryAddress(basketId.value, request, deliverableId)
            .then(utilitiesService.unwrapModel);
        state.basket = response ?? {} as BasketCheckoutModel;
    };

    const getPickUpErrorMessageId = (id: number) => `pickUpErrorMessageId-${id}`;

    const setDeliveryTime = async(deliverableId: number, request: SetDeliveryTimeRequest, messageId?: string): Promise<void> => {
        const response = await STOREClientService
            .setDeliveryTime(basketId.value, deliverableId, request, messageId)
            .then(utilitiesService.unwrapModel);
        state.basket = response ?? {} as BasketCheckoutModel;
    };

    const setCommentForHomeDelivery = async(deliverableId: number, request: SetCustomerDeliveryCommentRequest, messageId?: string): Promise<void> => {
        const response = await STOREClientService
            .setCommentForHomeDelivery(basketId.value, deliverableId, request, messageId)
            .then(utilitiesService.unwrapModel);
        state.basket = response ?? {} as BasketCheckoutModel;
    };

    const addInvoiceAddress = async(request: AddInvoiceAddressToBasketRequest): Promise<void> => {
        const response = await STOREClientService
            .addInvoiceAddress(basketId.value, request)
            .then(utilitiesService.unwrapModel);
        state.basket = response ?? {} as BasketCheckoutModel;
    };

    const setDeliveryInformationForItems = async(deliverableId: number): Promise<void> => {
        const response = await STOREClientService
            .getEarliestDeliveryDateForCustomerDelivery(basketId.value, deliverableId)
            .then(utilitiesService.unwrapModel);
        if (response) {
            state.deliveryInfo[deliverableId] = response;
        };
    };

    const clearDeliveryInformationForItems = (deliverableId: number): void => {
        if (state.deliveryInfo[deliverableId]) {
            delete state.deliveryInfo[deliverableId];
        }
    };

    const getDeliveryTimesForCustomerDelivery = async(deliverableId: number): Promise<DeliveryTimesForCustomerDelivery> => {
        const response = await STOREClientService
            .getDeliveryTimesForCustomerDelivery(basketId.value, deliverableId)
            .then(utilitiesService.unwrapModel);
        return response ?? {} as DeliveryTimesForCustomerDelivery;
    };

    const addDeliveryOption = async(deliverableId: number, itemId: string): Promise<void> => {
        const response = await STOREClientService
            .addDeliveryOption(basketId.value, deliverableId, itemId)
            .then(utilitiesService.unwrapModel);
        state.basket = response ?? {} as BasketCheckoutModel;
    };

    const removeDeliveryOption = async(deliverableId: number, itemId: string): Promise<void> => {
        const response = await STOREClientService
            .removeDeliveryOption(basketId.value, deliverableId, itemId)
            .then(utilitiesService.unwrapModel);
        state.basket = response ?? {} as BasketCheckoutModel;
    };

    const getDeliveryOptions = async(countryCode: string): Promise<(DeliveryOptionModel|undefined)[]> => {
        const response = await STOREClientService
            .getDeliveryOptions(countryCode, basketId.value)
            .then(utilitiesService.unwrapModel);
        return response ?? [] as DeliveryOptionModel[];
    };

    const getDeliveryOptionsForClickAndCollect = async(): Promise<(DeliveryOptionModel|undefined)[]> => {
        const response = await STOREClientService
            .getDeliveryOptionsForClickAndCollect(basketId.value)
            .then(utilitiesService.unwrapModel);
        return response ?? [] as DeliveryOptionModel[];
    };

    const getPickUpPointsForDelivery = async(deliverableId: number): Promise<PickUpPointsForDelivery> => {
        const response = await STOREClientService
            .getPickUpPointsForDelivery(basketId.value, deliverableId)
            .then(utilitiesService.unwrapModel);
        return response ?? {} as PickUpPointsForDelivery;
    };

    const getPickUpTimesForCustomerDelivery = async(deliverableId: number, hubId: string): Promise<PickUpTimesForCustomerDelivery> => {
        const response = await STOREClientService
            .getPickUpTimesForCustomerDelivery(basketId.value, deliverableId, hubId)
            .then(utilitiesService.unwrapModel);
        return response ?? {} as PickUpTimesForCustomerDelivery;
    };

    const changeClickAndCollectStoreFromCheckOut = async(storeId: string): Promise<void> => {
        const response = await STOREClientService
            .changeClickAndCollectStoreFromCheckOut(basketId.value, storeId)
            .then(utilitiesService.unwrapModel);
        state.basket = response ?? {} as BasketCheckoutModel;
    };

    const splitDelivery = async(deliveryId: number, itemIds: number[]): Promise<void> => {
        const response = await STOREClientService
            .splitDelivery(basketId.value, deliveryId, itemIds)
            .then(utilitiesService.unwrapModel);
        state.basket = response ?? {} as BasketCheckoutModel;
    };

    const removeSplitDelivery = async(deliveryId: number): Promise<void> => {
        const response = await STOREClientService
            .removeSplitDelivery(basketId.value, deliveryId)
            .then(utilitiesService.unwrapModel);
        state.basket = response ?? {} as BasketCheckoutModel;
    };

    const removeSplitDeliveries = async(deliveryId: number): Promise<BasketCheckoutModel> => {
        const response = await STOREClientService
            .removeSplitDeliveries(basketId.value, deliveryId)
            .then(utilitiesService.unwrapModel);
        state.basket = response ?? {} as BasketCheckoutModel;
        return state.basket;
    };

    const setSelectedTerm = async(termId) => {
        const response = await STOREClientService
            .setTermsAndConditions(basketId.value, termId)
            .then(utilitiesService.unwrapModel);
        state.basket = response ?? {} as BasketCheckoutModel;
    };

    const getDeliveryDateAndFreightPriceForSplitItems = async(deliveryId: number, itemIds: number[]): Promise<DeliveryDateAndFreightPriceForSplitItemsInCustomerDelivery> => {
        const response = await STOREClientService
            .getDeliveryDateAndFreightPriceForSplitItemsInCustomerDelivery(basketId.value, deliveryId, itemIds)
            .then(utilitiesService.unwrapModel);
        return response ?? {} as DeliveryDateAndFreightPriceForSplitItemsInCustomerDelivery;
    };

    const getCustomerAddressSuggestionByEmail = async(email: string, cancellationKey: string): Promise<CustomerAddressSuggestionModel | undefined> => {
        const response = await STOREClientService
            .getCustomerAddressSuggestionByEmail(email, cancellationKey, getCustomerAddressSuggestionByEmailMessageId)
            .then(utilitiesService.unwrapModel);
        return response;
    };

    const toggleCustomerMyBoliaSubscription = async(subscribe: boolean): Promise<BasketCheckoutModel> => {
        const response = await STOREClientService
            .toggleCustomerMyBoliaSubscription(basketId.value, subscribe)
            .then(utilitiesService.unwrapModel);
        state.basket = response ?? {} as BasketCheckoutModel;
        return state.basket;
    };

    const getNearestParcelShopsForDelivery = async(deliveryId: number, amount: number): Promise<ParcelShopModel[]> => {
        const response = await STOREClientService
            .getNearestParcelShopsForDelivery(basketId.value, deliveryId, amount)
            .then(utilitiesService.unwrapModel);
        return response ?? {} as ParcelShopModel[];
    };

    const getParcelShopsForZipCode = async(postalCode: string): Promise<ParcelShopModel[]> => {
        const response = await STOREClientService
            .getParcelShopsForZipCode(postalCode, basketId.value)
            .then(utilitiesService.unwrapModel);
        return response ?? {} as ParcelShopModel[];
    };

    const setPickAndPackToParcelShopDelivery = async(parcelShop: ParcelShopModel): Promise<void> => {
        const response = await STOREClientService
            .setPickAndPackToParcelShopDelivery(basketId.value, parcelShop)
            .then(utilitiesService.unwrapModel);
        state.basket = response ?? {} as BasketCheckoutModel;
    };

    const setPickAndPackToHomeDelivery = async(): Promise<void> => {
        const response = await STOREClientService
            .setPickAndPackToHomeDelivery(basketId.value)
            .then(utilitiesService.unwrapModel);
        state.basket = response ?? {} as BasketCheckoutModel;
    };

    const setSelectedPaymentOption = (option: PaymentOption | null): void => {
        state.selectedPaymentOption = option;
    };

    const setPaymentAddressValidationHandler = (handler: () => Promise<boolean>): void => {
        state.paymentAddressValidationHandler = handler;
    };

    const initiatePayment = async(option: PaymentOption): Promise<void> => {
        setSelectedPaymentOption(option);
        if (state.paymentAddressValidationHandler) {
            await state.paymentAddressValidationHandler();
        }
    };

    const setItemIdsOutOfStock = (items?: number[]): void => {
        state.itemIdsOutOfStock = items?.length ? items : [];
    };

    const clearItemIdsOutOfStock = (): void => {
        state.itemIdsOutOfStock.length = 0;
    };

    const setEarliestDeliveryDateChanged = (hasChanged: boolean): void => {
        state.hasDeliveryDateChanged = hasChanged;
    };

    const gotoPOS = async(request: CheckOutPosRequest): Promise<void> => {
        const response = await STOREClientService
            .checkOutPos(basketId.value, request)
            .then(utilitiesService.unwrapModel);

        if (response?.itemsOutOfStock) {
            setItemIdsOutOfStock(response?.itemsOutOfStock);
            return;
        }

        if (response?.requiredEarliestDeliveryDate) {
            setEarliestDeliveryDateChanged(true);
            return;
        }

        if (!response?.isCheckoutAble) {
            return;
        }

        if (response?.isPos2Enabled && response?.redirectUrl) {
            setIsPaymentReady(false);
            window.location.href = response.redirectUrl;
        } else if (response?.redirectUrl) {
            launchPOS(response.redirectUrl, response?.posIntegrationExtensionId);
            setIsPaymentReady(false);
        }
    };

    const gotoOnlinePayment = async(request: CheckOutOnlinePaymentRequest): Promise<void> => {
        const response = await STOREClientService
            .checkOutOnlinePayment(basketId.value, request)
            .then(utilitiesService.unwrapModel);
        if (response?.isCheckoutAble && response?.redirectUrl) {
            hardRedirect(response.redirectUrl);
        }
        setItemIdsOutOfStock(response?.itemsOutOfStock);
        if (response?.requiredEarliestDeliveryDate) {
            setEarliestDeliveryDateChanged(true);
        }
    };

    const gotoBankTransfer = async(request: CheckOutBankTransferPaymentRequest): Promise<void> => {
        const response = await STOREClientService.submitBankTransferPayment(basketId.value, request);
        if (response?.model?.receiptLink) {
            hardRedirect(response.model.receiptLink);
        }
        setItemIdsOutOfStock(response?.model?.itemsOutOfStock);
        if (response?.model?.requiredEarliestDeliveryDate) {
            setEarliestDeliveryDateChanged(true);
        }
    };

    const setIsPaymentReady = (ready: boolean): void => {
        state.isPaymentReady = ready;
    };

    const updateStep = (step: number): void => {
        stepQuery.value = step;
    };

    const gotoNextStep = (): void => {
        if (!isLastStep.value) {
            stepQuery.value = state.step + 1;
        }
    };

    const setShowBankTransferPaymentDialog = (show: boolean): void => {
        state.showBankTransferPaymentDialog = show;
    };

    watch(stepQuery, step => {
        state.step = step;
    }, { immediate: true });

    return {
        items,
        summary,
        invoiceAddress,
        deliveryInfo,
        deliverableCount,
        basket,
        step,
        isAddressStep,
        isDeliverableStep,
        isLastStep,
        selectedPaymentOption,
        defaultPaymentOption,
        isPaymentReady,
        itemIdsOutOfStock,
        hasDeliveryDateChanged,
        showBankTransferPaymentDialog,
        init,
        setDeliveryAddress,
        addInvoiceAddress,
        setDeliveryInformationForItems,
        clearDeliveryInformationForItems,
        getPickUpErrorMessageId,
        setDeliveryTime,
        setCommentForHomeDelivery,
        getDeliveryTimesForCustomerDelivery,
        getDeliveryOptions,
        getDeliveryOptionsForClickAndCollect,
        addDeliveryOption,
        removeDeliveryOption,
        getPickUpPointsForDelivery,
        getPickUpTimesForCustomerDelivery,
        changeClickAndCollectStoreFromCheckOut,
        initiatePayment,
        gotoPOS,
        gotoOnlinePayment,
        gotoBankTransfer,
        setSelectedPaymentOption,
        setPaymentAddressValidationHandler,
        setIsPaymentReady,
        clearItemIdsOutOfStock,
        setEarliestDeliveryDateChanged,
        updateStep,
        gotoNextStep,
        splitDelivery,
        removeSplitDelivery,
        removeSplitDeliveries,
        getDeliveryDateAndFreightPriceForSplitItems,
        getCustomerAddressSuggestionByEmail,
        toggleCustomerMyBoliaSubscription,
        setSelectedTerm,
        getNearestParcelShopsForDelivery,
        getParcelShopsForZipCode,
        setPickAndPackToParcelShopDelivery,
        setPickAndPackToHomeDelivery,
        setShowBankTransferPaymentDialog,
    };
}
